<template>
    <div class="test-android-webview">
        <div class="container mt-4">
            <h2>Тест Android WebView File Selector</h2>
            
            <div class="row">
                <div class="col-md-6">
                    <div class="card">
                        <div class="card-header">
                            <h5>Информация о среде</h5>
                        </div>
                        <div class="card-body">
                            <p><strong>User Agent:</strong> {{ userAgent }}</p>
                            <p><strong>Android WebView:</strong> {{ isAndroidWebView ? 'Да' : 'Нет' }}</p>
                            <p><strong>Версия Android:</strong> {{ androidVersion || 'Не определена' }}</p>
                            <p><strong>Поддержка multiple:</strong> {{ supportsMultiple ? 'Да' : 'Нет' }}</p>
                            <p><strong>Telegram Web App:</strong> {{ isTelegramWebApp ? 'Да' : 'Нет' }}</p>
                            <p><strong>Используемый селектор:</strong> {{ currentSelector }}</p>
                        </div>
                    </div>
                </div>
                
                <div class="col-md-6">
                    <div class="card">
                        <div class="card-header">
                            <h5>Выбор файлов</h5>
                        </div>
                        <div class="card-body">
                            <!-- Telegram Web App -->
                            <div v-if="isTelegramWebApp" class="mb-3">
                                <b-button @click="openTelegramFilePicker" variant="primary">
                                    <i class="fas fa-camera mr-2"></i>
                                    Выбрать файлы через Telegram
                                </b-button>
                            </div>
                            
                            <!-- Android WebView workaround -->
                            <div v-else-if="needsAndroidWorkaround" class="mb-3">
                                <AndroidFileSelector
                                    v-model="selectedFiles"
                                    :multiple="true"
                                    accept="image/*, video/*"
                                    :max-count="5"
                                    @change="onFilesChange"
                                />
                            </div>
                            
                            <!-- Стандартный input -->
                            <div v-else class="mb-3">
                                <label class="form-label">Стандартный input с multiple:</label>
                                <input 
                                    type="file" 
                                    multiple 
                                    @change="handleStandardFiles" 
                                    accept="image/*, video/*" 
                                    class="form-control"
                                />
                            </div>
                            
                            <!-- Кнопка очистки -->
                            <b-button 
                                v-if="selectedFiles.length > 0"
                                @click="clearFiles" 
                                variant="danger" 
                                size="sm"
                            >
                                Очистить все файлы
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Список выбранных файлов -->
            <div class="row mt-4">
                <div class="col-12">
                    <div class="card">
                        <div class="card-header">
                            <h5>Выбранные файлы ({{ selectedFiles.length }})</h5>
                        </div>
                        <div class="card-body">
                            <div v-if="selectedFiles.length === 0" class="text-muted">
                                Файлы не выбраны
                            </div>
                            <div v-else class="row">
                                <div 
                                    v-for="(file, index) in selectedFiles" 
                                    :key="index"
                                    class="col-md-4 mb-3"
                                >
                                    <div class="card">
                                        <div class="card-body">
                                            <div class="d-flex align-items-center">
                                                <i class="fas fa-file-image mr-2 text-primary"></i>
                                                <div class="flex-grow-1">
                                                    <div class="font-weight-bold">{{ file.name }}</div>
                                                    <div class="text-muted small">{{ formatFileSize(file.size) }}</div>
                                                    <div class="text-muted small">{{ file.type }}</div>
                                                </div>
                                                <b-button 
                                                    @click="removeFile(index)"
                                                    variant="outline-danger" 
                                                    size="sm"
                                                >
                                                    <i class="fas fa-times"></i>
                                                </b-button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { 
    isAndroidWebView, 
    supportsMultipleFileSelection,
    getAndroidVersion 
} from '@/utils/androidWebView';
import { 
    isTelegramWebAppAvailable, 
    requestTelegramMedia, 
    convertTelegramFilesToFileObjects 
} from '@/utils/telegramWebApp';
import AndroidFileSelector from '@/components/AndroidFileSelector/AndroidFileSelector.vue';

export default {
    name: 'TestAndroidWebView',
    components: {
        AndroidFileSelector
    },
    data() {
        return {
            selectedFiles: [],
            userAgent: navigator.userAgent
        };
    },
    computed: {
        isAndroidWebView() {
            return isAndroidWebView();
        },
        androidVersion() {
            return getAndroidVersion();
        },
        supportsMultiple() {
            return supportsMultipleFileSelection();
        },
        isTelegramWebApp() {
            return isTelegramWebAppAvailable();
        },
        needsAndroidWorkaround() {
            return this.isAndroidWebView && !this.supportsMultiple;
        },
        currentSelector() {
            if (this.isTelegramWebApp) {
                return 'Telegram Web App API';
            } else if (this.needsAndroidWorkaround) {
                return 'Android WebView Workaround';
            } else {
                return 'Стандартный input multiple';
            }
        }
    },
    methods: {
        handleStandardFiles(event) {
            const files = Array.from(event.target.files);
            this.selectedFiles = files;
        },
        
        onFilesChange(files) {
            this.selectedFiles = files;
        },
        
        async openTelegramFilePicker() {
            try {
                const result = await requestTelegramMedia({
                    type: 'photo',
                    multiple: true,
                    maxCount: 5
                });
                
                if (result && result.length > 0) {
                    const files = await convertTelegramFilesToFileObjects(result);
                    this.selectedFiles = files;
                }
            } catch (error) {
                console.error('Ошибка при выборе файлов в Telegram:', error);
                this.$bvToast.toast('Ошибка при выборе файлов', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
            }
        },
        
        removeFile(index) {
            this.selectedFiles.splice(index, 1);
        },
        
        clearFiles() {
            this.selectedFiles = [];
        },
        
        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        }
    }
};
</script>

<style scoped>
.test-android-webview {
    min-height: 100vh;
    background-color: #f8f9fa;
}

.card {
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.card-header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}
</style> 